import {
  lazyPipe,
  validateApiRequest,
  validateApiResponse,
  withApiErrorCapture,
} from '@musinsa/apis-common-api-zod4';
import { executeStandardRequest } from '@musinsa/packages-http';
import { z } from 'zod';
import { createApiResponseSchema } from '../../utils';
import { partnerApiPath } from '../apiPath';
import { partnerHttpClient } from '../httpClient';

const PartnerCampaignTypeSchema = z
  .enum(['PROPOSAL', 'RECRUITMENT'])
  .describe('PartnerCampaignTypeSchema');

export type PartnerCampaignType = z.infer<typeof PartnerCampaignTypeSchema>;

// NOTE(CBPFE-4088): BE Swagger 정합 — BE 는 `TWITTER` (X 아님). 다른 곳들도 동일.
const PartnerCampaignSnsChannelSchema = z
  .enum(['INSTAGRAM', 'YOUTUBE', 'TWITTER', 'TIKTOK', 'NAVER_BLOG', 'THREADS', 'SNAP'])
  .describe('PartnerCampaignSnsChannelSchema');

export type PartnerCampaignSnsChannel = z.infer<typeof PartnerCampaignSnsChannelSchema>;

const PartnerCampaignContentTypeSchema = z
  .enum(['IMAGE', 'SHORT_FORM', 'LONG_FORM', 'TEXT', 'BLOG_POST'])
  .describe('PartnerCampaignContentTypeSchema');

export type PartnerCampaignContentType = z.infer<typeof PartnerCampaignContentTypeSchema>;

const CampaignGoodsSchema = z
  .object({
    goodsNo: z.number(),
    checkedValueIds: z.array(z.number()),
    extraValueIds: z.array(z.number().int()).optional(),
  })
  .describe('CampaignGoodsSchema');

export type CampaignGoods = z.infer<typeof CampaignGoodsSchema>;

const PostPartnerCampaignRequestQuerySchema = z
  .object({
    platform: z.string(),
  })
  .describe('PostPartnerCampaignRequestQuerySchema');

// 동의 항목 6→5 재편 (SID-4593). PRODUCT_ORDER_V1 은 OPERATION_V1 로 병합,
// CONTENT_USAGE_V1 은 정식 명칭에 맞춰 CURATOR_INFO_HANDLING_V1 로 변경.
const CampaignConsentTypeSchema = z
  .enum([
    'RESPONSIBILITY_V1',
    'OPERATION_V1',
    'AD_GUIDELINE_V1',
    'TAX_V1',
    'CURATOR_INFO_HANDLING_V1',
  ])
  .describe('CampaignConsentTypeSchema');

const CampaignConsentRequestSchema = z
  .object({
    agreedConsentTypes: z.array(CampaignConsentTypeSchema),
    agreementPath: z.string().max(512).nullable().optional(),
  })
  .describe('CampaignConsentRequestSchema');

const PostPartnerCampaignRequestBodySchema = z
  .object({
    brandId: z.string(),
    campaignType: PartnerCampaignTypeSchema,
    title: z.string().max(100).nullable().optional(),
    goods: z.array(CampaignGoodsSchema).min(1),
    snsChannels: z.array(PartnerCampaignSnsChannelSchema).min(1),
    contentTypes: z.array(PartnerCampaignContentTypeSchema).min(1),
    requiredHashtags: z.array(z.string()).min(1).max(5),
    requestNote: z.string().max(500).nullable().optional(),
    maxParticipants: z.number().int().min(1).max(20).nullable().optional(),
    recruitmentStartAt: z.string().nullable().optional(),
    recruitmentEndAt: z.string().nullable().optional(),
    consent: CampaignConsentRequestSchema.optional(),
  })
  .describe('PostPartnerCampaignRequestBodySchema');

const PostPartnerCampaignRequestSchema = z.object({
  query: PostPartnerCampaignRequestQuerySchema,
  body: PostPartnerCampaignRequestBodySchema,
});

export type PostPartnerCampaignRequest = z.infer<typeof PostPartnerCampaignRequestSchema>;

const PostPartnerCampaignResponseDataSchema = z.unknown();
const PostPartnerCampaignResponseSchema = createApiResponseSchema(
  PostPartnerCampaignResponseDataSchema,
);

export type PostPartnerCampaignResponse = z.infer<typeof PostPartnerCampaignResponseSchema>;

/** POST `/v1/partner/campaigns` — 시딩 캠페인 생성 */
export const postPartnerCampaign = withApiErrorCapture(
  lazyPipe(
    validateApiRequest(PostPartnerCampaignRequestSchema),
    async (request: PostPartnerCampaignRequest) =>
      executeStandardRequest(async () =>
        partnerHttpClient.post<PostPartnerCampaignResponse>(
          partnerApiPath.postCampaign,
          request.body,
          { params: request.query },
        ),
      ),
    validateApiResponse(PostPartnerCampaignResponseSchema),
  ),
);
